/**
 * VR controller and hand models (XrControllers loads them from the WebXR Input Profiles CDN) use
 * lit materials, and most scenes have no lights and no ambient, so the models drew solid black.
 * They are shown self-lit instead: each material's base colour and texture become its emissive
 * output, so the controllers look the same in every scene. Scene lights, the ambient and every
 * other material are left alone (splat relighting reads the scene's lights).
 */
import * as pc from 'playcanvas';

/** The self-lit copy of each model material: materials are shared by every input source that uses a model. */
const selfLit = new WeakMap<pc.Material, pc.Material>();
const isSelfLit = new WeakSet<pc.Material>();

/** A self-lit copy of a lit material: base colour and texture as emissive, no lighting, reflections or specular. */
export function selfLitControllerMaterial(source: pc.Material): pc.Material {
    if (!(source instanceof pc.StandardMaterial) || isSelfLit.has(source)) return source;
    const cached = selfLit.get(source);
    if (cached) return cached;
    const material = source.clone();
    material.name = `${source.name || 'controller'} (self-lit)`;
    material.emissive.copy(source.diffuse);
    material.emissiveIntensity = 1;
    material.emissiveMap = source.diffuseMap;
    material.emissiveMapChannel = source.diffuseMapChannel;
    material.emissiveMapUv = source.diffuseMapUv;
    material.emissiveMapTiling.copy(source.diffuseMapTiling);
    material.emissiveMapOffset.copy(source.diffuseMapOffset);
    material.emissiveMapRotation = source.diffuseMapRotation;
    material.emissiveVertexColor = source.diffuseVertexColor;
    material.emissiveVertexColorChannel = source.diffuseVertexColorChannel;
    material.diffuse.set(0, 0, 0);
    material.diffuseMap = null;
    material.diffuseVertexColor = false;
    material.useLighting = false;
    material.useSkybox = false;
    material.useMetalness = false;
    material.specular.set(0, 0, 0);
    material.specularMap = null;
    material.update();
    selfLit.set(source, material);
    isSelfLit.add(material);
    return material;
}

/** Shows every mesh of a controller or hand model self-lit. */
export function showControllerSelfLit(entity: pc.Entity): void {
    for (const render of entity.findComponents('render') as pc.RenderComponent[]) {
        for (const meshInstance of render.meshInstances) {
            if (meshInstance.material) meshInstance.material = selfLitControllerMaterial(meshInstance.material);
        }
    }
}
